/**
 * The app-wide "Querying the datalake" state.
 *
 * Every data request the app makes is a react-query fetch, so one tracker
 * watches them all and no page carries any code for it. This module is the
 * state machine and nothing else: no React, no react-query. The hook in
 * `src/hooks/use-query-activity.ts` feeds it, and
 * `src/design-system/query-progress.tsx` draws what it reports.
 *
 * Overlapping queries form one batch. The batch is invisible for the first
 * 350 ms, so a quick or cached answer never flashes. It then shows how many of
 * its queries are done and how long it has taken. When the last query ends it
 * says "Loaded" briefly, or "Query failed" when any of them failed, then fades.
 */

/** A batch shows only once it has been running this long. */
export const SHOW_DELAY_MS = 350
/** After this long the text admits the wait is long. */
export const SLOW_AFTER_MS = 20_000
/** How long "Loaded" stays before it fades. */
export const LOADED_HOLD_MS = 900
/** How long "Query failed" stays before it fades. The page's own panel explains it. */
export const FAILED_HOLD_MS = 5000
/** The fade-out, which the indicator's `duration-300` opacity transition matches. */
export const FADE_MS = 300
/** The elapsed-seconds and fill refresh. */
export const TICK_MS = 1000
/** The fill a running batch creeps toward and never passes. */
export const FILL_CEILING = 0.9
/** A visible batch never draws an empty bar. */
const FILL_FLOOR = 0.04
/** The creep's time constant: about 63% of the way to the ceiling in 8 s. */
const CREEP_TIME_CONSTANT_MS = 8000

/**
 * `meta` for a query that does not read the datalake: settings, access,
 * catalogues, saved documents. The indicator does not count it. A query with
 * a `refetchInterval` is left out without it.
 *
 * Every `useQuery` that shares one key must pass the same `meta`, because the
 * cache keeps the options of the observer that rendered last.
 */
export const QUIET_QUERY_META = { quiet: true } as const

/**
 * The time source the tracker reads. Production uses `systemClock`; a test
 * passes a clock it moves by hand, so no test needs fake timers.
 */
export interface QueryActivityClock {
  now(): number
  /**
   * Run `run` once, after `ms`.
   *
   * @returns A function that cancels the run.
   */
  after(ms: number, run: () => void): () => void
}

/** The wall clock and the browser's timers. */
export const systemClock: QueryActivityClock = {
  now: () => Date.now(),
  after: (ms, run) => {
    const handle = setTimeout(run, ms)

    return () => clearTimeout(handle)
  }
}

export type QueryActivityPhase = 'idle' | 'running' | 'loaded' | 'failed'

export interface QueryActivitySnapshot {
  phase: QueryActivityPhase
  /** Whether the indicator is on screen at all. */
  visible: boolean
  /** True during the fade-out that follows `loaded` or `failed`. */
  fading: boolean
  /** Queries started in this batch, including those already done. */
  total: number
  /** Queries that have ended, in success or failure. */
  done: number
  elapsedSeconds: number
  /** Past 20 s. */
  slow: boolean
  /** 0 to 1. An estimate: a running batch never passes 0.9. */
  fill: number
}

const IDLE: QueryActivitySnapshot = {
  phase: 'idle',
  visible: false,
  fading: false,
  total: 0,
  done: 0,
  elapsedSeconds: 0,
  slow: false,
  fill: 0
}

/**
 * The words for a snapshot.
 *
 * `valueText` leaves the seconds out so a screen reader is told when the
 * counts or the state change, not once a second.
 *
 * @param snapshot - The tracker's snapshot.
 * @returns The visible label, the elapsed text and the progressbar's value text.
 */
export function queryActivityText(snapshot: QueryActivitySnapshot): {
  label: string
  elapsed: string
  valueText: string
} {
  if (snapshot.phase === 'failed') {
    return { label: 'Query failed', elapsed: '', valueText: 'Query failed' }
  }
  if (snapshot.phase === 'loaded') {
    return { label: 'Loaded', elapsed: '', valueText: 'Loaded' }
  }
  const counts =
    snapshot.total > 1 ? ` ${snapshot.done} of ${snapshot.total} done` : ''
  const label = `Querying the datalake…${counts}`
  const slow = snapshot.slow ? ', still working, large ranges take longer' : ''

  return {
    label,
    elapsed: `${snapshot.elapsedSeconds}s`,
    valueText: `${label}${slow}`
  }
}

type Listener = () => void
type Timer = 'show' | 'tick' | 'hold' | 'fade'

/**
 * QueryActivityTracker — counts in-flight queries into batches.
 *
 * `set` is idempotent per id, so the caller may report the same query on every
 * cache event. It owns its timers; `reset` cancels them and forgets the batch.
 */
export class QueryActivityTracker {
  private snapshot: QueryActivitySnapshot = IDLE
  private readonly listeners = new Set<Listener>()
  private readonly running = new Set<string>()
  private readonly timers = new Map<Timer, () => void>()
  private total = 0
  private done = 0
  private failed = 0
  private startedAt = 0
  private ended = true
  private shown = false

  /**
   * @param clock - The time source. Defaults to the wall clock.
   */
  constructor(private readonly clock: QueryActivityClock = systemClock) {}

  /** Subscribe for `useSyncExternalStore`. Returns the unsubscribe. */
  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener)

    return () => {
      this.listeners.delete(listener)
    }
  }

  /** The current snapshot. The same object until something changes. */
  getSnapshot = (): QueryActivitySnapshot => this.snapshot

  /**
   * Report one query's state.
   *
   * @param id - A stable id for the query.
   * @param state - `running`, or how it ended.
   */
  set(id: string, state: 'running' | 'ok' | 'error'): void {
    if (state === 'running') {
      if (!this.running.has(id)) this.begin(id)

      return
    }
    if (this.running.has(id)) this.finish(id, state === 'ok')
  }

  /** Cancel every timer and forget the batch. */
  reset(): void {
    this.cancelAll()
    this.running.clear()
    this.total = 0
    this.done = 0
    this.failed = 0
    this.ended = true
    this.shown = false
    this.publish(IDLE)
  }

  private begin(id: string): void {
    if (this.ended) {
      // A batch that starts while "Loaded" or "Query failed" is still on
      // screen continues from it: nothing new appears, so nothing can flash.
      const carriedOn = this.snapshot.visible
      this.cancelAll()
      this.total = 0
      this.done = 0
      this.failed = 0
      this.shown = carriedOn
      this.ended = false
      this.startedAt = this.clock.now()
      this.running.add(id)
      this.total = 1
      if (carriedOn) {
        this.tick()
      } else {
        this.schedule('show', SHOW_DELAY_MS, () => {
          this.shown = true
          this.tick()
        })
      }

      return
    }
    this.running.add(id)
    this.total += 1
    if (this.shown) this.refresh()
  }

  /** Refresh now, then once a second while the batch runs. */
  private tick(): void {
    this.refresh()
    this.schedule('tick', TICK_MS, () => this.tick())
  }

  private finish(id: string, ok: boolean): void {
    this.running.delete(id)
    this.done += 1
    if (!ok) this.failed += 1
    if (this.running.size > 0) {
      if (this.shown) this.refresh()

      return
    }
    this.end()
  }

  private end(): void {
    this.ended = true
    this.cancel('show')
    this.cancel('tick')
    if (this.failed > 0) {
      // A failure is worth a glance even when it came back fast.
      this.shown = true
      this.settle('failed', FAILED_HOLD_MS)

      return
    }
    if (!this.shown) {
      // Quick or cached: it never appeared, so it never shows.
      this.publish(IDLE)

      return
    }
    this.settle('loaded', LOADED_HOLD_MS)
  }

  private settle(phase: 'loaded' | 'failed', holdMs: number): void {
    this.publish({ ...this.measure(phase), fill: 1 })
    this.schedule('hold', holdMs, () => {
      this.publish({ ...this.measure(phase), fill: 1, fading: true })
      this.schedule('fade', FADE_MS, () => {
        this.shown = false
        this.publish(IDLE)
      })
    })
  }

  private refresh(): void {
    this.publish(this.measure('running'))
  }

  private measure(phase: QueryActivityPhase): QueryActivitySnapshot {
    const elapsedMs = Math.max(0, this.clock.now() - this.startedAt)
    const running = this.total - this.done
    const creep = 1 - Math.exp(-elapsedMs / CREEP_TIME_CONSTANT_MS)
    const share =
      this.total === 0
        ? 0
        : (this.done + running * FILL_CEILING * creep) / this.total

    return {
      phase,
      visible: true,
      fading: false,
      total: this.total,
      done: this.done,
      elapsedSeconds: Math.floor(elapsedMs / 1000),
      slow: elapsedMs >= SLOW_AFTER_MS,
      fill: Math.max(FILL_FLOOR, Math.min(FILL_CEILING, share))
    }
  }

  private publish(next: QueryActivitySnapshot): void {
    this.snapshot = next
    for (const listener of this.listeners) listener()
  }

  private schedule(timer: Timer, ms: number, run: () => void): void {
    this.timers.set(
      timer,
      this.clock.after(ms, () => {
        this.timers.delete(timer)
        run()
      })
    )
  }

  private cancel(timer: Timer): void {
    this.timers.get(timer)?.()
    this.timers.delete(timer)
  }

  private cancelAll(): void {
    for (const cancel of this.timers.values()) cancel()
    this.timers.clear()
  }
}
